import { GRAYS, PALETTE } from "@eldrun/content";
import { BufferAttribute, Color, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, TetrahedronGeometry, type Material, type Object3D } from "three";
import { SHARD_GLOW, createShardsMaterial } from "../render/shaders/shards.ts";
import type { DebrisPiece } from "./debris.ts";

/** Teto de estilhaços vivos (o mais antigo cede lugar). */
export const SHARD_POOL = 480;
/** Teto do entulho (F90), à parte: um bloco que quebra não apaga os estilhaços do combate. */
export const RUBBLE_POOL = 256;
/** Teto da camada ambiente (F90): vagalumes à noite e poeira de dia, em volta da câmera. */
export const AMBIENT_MAX = 120;
/** Teto dos cristais grandes (Fase 14) no Alto; quem chama passa o do tier (`CRYSTALS`). */
export const CRYSTAL_POOL = 24;
/** Cristais vivos por tier (Fase 14): nenhum no Baixo. */
export const CRYSTALS = { baixo: 0, medio: 12, alto: CRYSTAL_POOL } as const;
/** Contas de fio por quadro (os fios do Brado e do reviver), fora do pool. */
const BEADS = 96;
const GRAVITY = 9;
/** Arrasto dos estilhaços (fração da velocidade perdida por segundo): o jorro abre rápido e assenta. */
export const SHARD_DRAG = 3;
/** Entulho: gravidade de verdade, quique (restituição) e atrito no chão, e quando para. */
const RUBBLE_GRAVITY = 9.8;
const RESTITUTION = 0.3;
const FRICTION = 0.55;
const SETTLE_SPEED = 0.6;
/** Camada ambiente: o lado do quadrado em volta da câmera (m), que se repete pelo mundo. */
const AMBIENT_SPAN = 28;

/** Um jorro de estilhaços. */
export interface ShardBurst {
  x: number;
  y: number;
  z: number;
  color: string;
  /** Cinza neutro (0 a 1) no lugar de `color`, já dividido pelo brilho: sem bloom (respingos, terra, capim). */
  gray?: number;
  n: number;
  /** Velocidade de saída (m/s); 0 deixa parado (rastro). */
  speed: number;
  size: number;
  /** Vida em segundos. */
  life: number;
  /** Quanto sobe a mais (0 a 1). */
  up?: number;
  /** Opacidade (os efeitos dos aliados vêm mais fracos). */
  alpha?: number;
  /** Cai com a gravidade (o padrão); o rastro fica no ar. */
  fall?: boolean;
  /**
   * Faísca (Fase 14): o estilhaço vira um fio esticado na direção em que voa, `streak` vezes mais comprido
   * por m/s (preso entre 1 e 6 vezes); some com a velocidade.
   */
  streak?: number;
  /**
   * Velocidade somada à saída, sem a variação de cada estilhaço (m/s, no chão): o jorro varre para um lado
   * ou abre como uma onda (a assinatura dos inimigos maiores, F91).
   */
  dx?: number;
  dz?: number;
}

interface Shard {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  ax: number;
  ay: number;
  az: number;
  angle: number;
  spin: number;
  size: number;
  age: number;
  life: number;
  color: Color;
  alpha: number;
  fall: boolean;
  streak: number;
}

/** Um pedaço de entulho: cai, quica, assenta e encolhe no fim da vida. */
interface Rubble {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  ax: number;
  ay: number;
  az: number;
  angle: number;
  spin: number;
  size: number;
  age: number;
  life: number;
  color: Color;
  rest: boolean;
  /** Cristal (Fase 14): a lâmina (escala por eixo) e a opacidade; sem ele, entulho cinza. */
  crystal?: { sx: number; sy: number; sz: number; alpha: number };
}

/** Um jorro de cristais grandes (Fase 14, a referência): lâminas de vidro na cor de quem bateu que voam, quicam e assentam. */
export interface CrystalBurst {
  x: number;
  y: number;
  z: number;
  color: string;
  n: number;
  /** Tamanho (m) do maior. */
  size: number;
  speed: number;
  /** Vida (s). */
  life: number;
  /** Para onde o golpe empurra (m/s, no chão). */
  dx?: number;
  dz?: number;
  alpha?: number;
  /** Teto de cristais vivos (o do tier, `CRYSTALS`); cheio, o mais velho cede lugar. */
  cap?: number;
}

/** Como o entulho sai: velocidade pela normal do triângulo, quanto sobe e a vida (s). */
export interface RubbleBurst {
  speed: number;
  up: number;
  life: number;
  /** O estalo aceso no meio (acima de 1, o bloom pega); some com "Reduzir lampejos". */
  pop?: { x: number; y: number; z: number; size: number };
}

/** O que a camada ambiente lê a cada quadro. */
export interface AmbientFrame {
  /** Centro (a câmera) e o instante (s). */
  x: number;
  z: number;
  time: number;
  /** 0 de dia, 1 de noite: vagalumes à noite, poeira de dia. */
  night: number;
  /** Fração da densidade (a opção "Capim e detalhes"; 0 desliga). */
  density: number;
  /** "Reduzir lampejos": os vagalumes ficam acesos sem piscar. */
  steady: boolean;
}

/** Ângulo áureo: espalha as direções sem aleatório (o mesmo jorro, o mesmo desenho). */
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

/**
 * Estilhaços de combate (F68, doc 09 §4, guia §21): impacto, rastro de arma, morte em estilhaços e as
 * contas dos fios, num pool instanciado (um draw). Puro desenho do cliente; quem decide o acerto é o
 * servidor. Determinístico pela ordem dos jorros (sem `Math.random`).
 */
export class Shards {
  private readonly mesh: Mesh;
  private readonly geometry: InstancedBufferGeometry;
  private readonly center: InstancedBufferAttribute;
  private readonly spin: InstancedBufferAttribute;
  private readonly size: InstancedBufferAttribute;
  private readonly tint: InstancedBufferAttribute;
  private readonly shape: InstancedBufferAttribute;
  private readonly shards: Shard[] = [];
  private readonly colors = new Map<string, Color>();
  private readonly beads: { x: number; y: number; z: number; color: Color; size: number; alpha: number }[] = [];
  private readonly rubble: Rubble[] = [];
  private readonly crystalList: Rubble[] = [];
  /** Cinzas do entulho já divididos pelo brilho do shader (o entulho não passa de 1: sem bloom). */
  private readonly grays = new Map<number, Color>();
  private readonly ground: ((x: number, z: number, y: number) => number) | undefined;
  private readonly fireflyColor: Color;
  private readonly dustColor: Color;
  private ambientFrame: AmbientFrame | undefined;
  private seq = 0;

  /**
   * `ground`: a altura do chão em (x, z) para algo na altura y (o piso abaixo dele, não um teto acima),
   * onde o entulho quica e assenta e a camada ambiente pousa.
   */
  constructor(root: Object3D, ground?: (x: number, z: number, y: number) => number) {
    this.ground = ground;
    // Vagalume: o neutro, um pouco acima de 1 (o bloom pega de leve à noite); poeira: cinza claro e apagado.
    this.fireflyColor = new Color(PALETTE.neutro.hex).multiplyScalar(1.3 / SHARD_GLOW);
    this.dustColor = new Color().setScalar(GRAYS.claro / SHARD_GLOW);
    const tetra = new TetrahedronGeometry(1);
    this.geometry = new InstancedBufferGeometry();
    this.geometry.setAttribute("position", tetra.getAttribute("position"));
    // Os cantos de cada face (sem índice): as arestas dos cristais.
    const corners = tetra.getAttribute("position").count;
    const bary = new Float32Array(corners * 3);
    for (let i = 0; i < corners; i++) bary[i * 3 + (i % 3)] = 1;
    this.geometry.setAttribute("aBary", new BufferAttribute(bary, 3));
    const total = SHARD_POOL + RUBBLE_POOL + CRYSTAL_POOL + AMBIENT_MAX + BEADS;
    const attr = (n: number) => new InstancedBufferAttribute(new Float32Array(total * n), n).setUsage(DynamicDrawUsage);
    this.center = attr(3);
    this.spin = attr(4);
    this.size = attr(1);
    this.tint = attr(4);
    this.shape = attr(4);
    this.geometry.setAttribute("aShape", this.shape);
    this.geometry.setAttribute("aCenter", this.center);
    this.geometry.setAttribute("aSpin", this.spin);
    this.geometry.setAttribute("aSize", this.size);
    this.geometry.setAttribute("aColor", this.tint);
    this.geometry.instanceCount = 0;
    this.mesh = new Mesh(this.geometry, createShardsMaterial());
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 8;
    root.add(this.mesh);
  }

  burst(b: ShardBurst): void {
    const color = b.gray === undefined ? this.color(b.color) : this.gray(b.gray);
    for (let i = 0; i < b.n; i++) {
      if (this.shards.length >= SHARD_POOL) this.shards.shift();
      const k = this.seq++;
      // Direção na esfera (Fibonacci), puxada para cima pelo `up`.
      const t = (i + 0.5) / b.n;
      const yy = Math.min(1, 1 - 2 * t * (1 - (b.up ?? 0.3)));
      const r = Math.sqrt(Math.max(0, 1 - yy * yy));
      const a = k * GOLDEN;
      const jitter = 0.6 + 0.4 * (((k * 7919) % 97) / 97);
      const v = b.speed * jitter;
      this.shards.push({
        x: b.x,
        y: b.y,
        z: b.z,
        vx: Math.cos(a) * r * v + (b.dx ?? 0),
        vy: yy * v,
        vz: Math.sin(a) * r * v + (b.dz ?? 0),
        ax: Math.cos(a * 1.7),
        ay: 0.5,
        az: Math.sin(a * 1.7),
        angle: a,
        spin: 6 + (k % 5) * 2,
        size: b.size * (0.6 + 0.4 * jitter),
        age: 0,
        life: b.life * (0.7 + 0.3 * jitter),
        color,
        alpha: b.alpha ?? 1,
        fall: b.fall ?? true,
        streak: b.streak ?? 0,
      });
    }
  }

  /**
   * Cristais grandes (Fase 14): lâminas achatadas (um tetraedro esticado e fino) que saem do golpe para
   * cima e para o lado do empurrão, giram, quicam no chão, assentam e encolhem no último terço da vida.
   */
  crystals(b: CrystalBurst): void {
    const cap = Math.min(CRYSTAL_POOL, b.cap ?? CRYSTAL_POOL);
    if (cap <= 0) return;
    const color = this.color(b.color);
    for (let i = 0; i < b.n; i++) {
      while (this.crystalList.length >= cap) this.crystalList.shift();
      const k = this.seq++;
      const jitter = 0.6 + 0.4 * (((k * 7919) % 97) / 97);
      const a = k * GOLDEN;
      const t = (i + 0.5) / b.n;
      const v = b.speed * jitter;
      this.crystalList.push({
        x: b.x,
        y: b.y,
        z: b.z,
        vx: Math.cos(a) * v * 0.8 + (b.dx ?? 0),
        vy: (1.2 + 2.4 * (1 - t)) * jitter + v * 0.4,
        vz: Math.sin(a) * v * 0.8 + (b.dz ?? 0),
        ax: Math.cos(a * 1.3),
        ay: 0.4,
        az: Math.sin(a * 1.3),
        angle: a,
        spin: 3 + (k % 5),
        size: b.size * (0.5 + 0.5 * jitter),
        age: 0,
        life: b.life * (0.8 + 0.2 * jitter),
        color,
        rest: false,
        crystal: { sx: 0.7, sy: 1.5 + 0.8 * jitter, sz: 0.08, alpha: b.alpha ?? 1 },
      });
    }
  }

  /**
   * Entulho de um bloco que quebra (F90): um pedaço por item de `pieces` (os triângulos do modelo,
   * `debrisPieces`), saindo pela normal com um pouco de subida, no cinza dele. Com `pop`, o estalo aceso
   * no meio, um estilhaço parado que some em 0,12 s.
   */
  debris(pieces: readonly DebrisPiece[], b: RubbleBurst): void {
    for (const piece of pieces) {
      if (this.rubble.length >= RUBBLE_POOL) this.rubble.shift();
      const k = this.seq++;
      const jitter = 0.6 + 0.4 * (((k * 7919) % 97) / 97);
      const v = b.speed * jitter;
      const a = k * GOLDEN;
      this.rubble.push({
        x: piece.x,
        y: piece.y,
        z: piece.z,
        vx: piece.nx * v,
        vy: piece.ny * v + b.up * jitter,
        vz: piece.nz * v,
        ax: Math.cos(a * 1.7),
        ay: 0.5,
        az: Math.sin(a * 1.7),
        angle: a,
        spin: 4 + (k % 5) * 1.5,
        size: piece.size,
        age: 0,
        life: b.life * (0.8 + 0.2 * jitter),
        color: this.gray(piece.gray),
        rest: false,
      });
    }
    if (b.pop) this.burst({ ...b.pop, color: PALETTE.neutro.hex, n: 1, speed: 0, life: 0.12, fall: false });
  }

  /** A camada ambiente deste quadro (vagalumes e poeira em volta da câmera); `undefined` desliga. */
  ambient(frame: AmbientFrame | undefined): void {
    this.ambientFrame = frame;
  }

  /** Contas de um fio de `from` a `to` (só neste quadro), uma a cada `step` m. */
  tether(from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, hex: string, alpha = 1, step = 0.9): void {
    const d = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
    const n = Math.max(1, Math.floor(d / step));
    const color = this.color(hex);
    for (let i = 0; i <= n && this.beads.length < BEADS; i++) {
      const t = i / n;
      this.beads.push({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t, z: from.z + (to.z - from.z) * t, color, size: 0.06, alpha });
    }
  }

  update(dt: number): void {
    let i = 0;
    const s = Math.min(dt, 0.05);
    for (let k = this.shards.length - 1; k >= 0; k--) {
      const p = this.shards[k] as Shard;
      p.age += s;
      if (p.age >= p.life) {
        this.shards.splice(k, 1);
        continue;
      }
      if (p.fall) p.vy -= GRAVITY * s;
      // Arrasto: o jorro abre rápido e assenta.
      const drag = Math.max(0, 1 - SHARD_DRAG * s);
      p.vx *= drag;
      p.vz *= drag;
      p.x += p.vx * s;
      p.y += p.vy * s;
      p.z += p.vz * s;
      p.angle += p.spin * s;
      const f = 1 - p.age / p.life;
      if (p.streak > 0) {
        // Faísca: o +y do tetraedro na direção da velocidade (eixo = y × v, ângulo entre os dois).
        const speed = Math.hypot(p.vx, p.vy, p.vz);
        const stretch = Math.min(6, Math.max(1, p.streak * speed));
        const ny = speed > 1e-4 ? p.vy / speed : 1;
        this.write(i++, p.x, p.y, p.z, p.vz, 0, -p.vx, Math.acos(Math.max(-1, Math.min(1, ny))), p.size * (0.5 + 0.5 * f), p.color, p.alpha * Math.min(1, f * 2), 0.3, stretch, 0.3);
        continue;
      }
      this.write(i++, p.x, p.y, p.z, p.ax, p.ay, p.az, p.angle, p.size * (0.4 + 0.6 * f), p.color, p.alpha * Math.min(1, f * 2));
    }
    i = this.updateRubble(this.rubble, s, i);
    i = this.updateRubble(this.crystalList, s, i);
    i = this.writeAmbient(i);
    for (const b of this.beads) this.write(i++, b.x, b.y, b.z, 0, 1, 0, 0.785, b.size, b.color, b.alpha);
    this.beads.length = 0;
    this.geometry.instanceCount = i;
    for (const a of [this.center, this.spin, this.size, this.tint, this.shape]) a.needsUpdate = true;
  }

  get count(): number {
    return this.geometry.instanceCount;
  }

  /** Vivos no pool (sem as contas). */
  get live(): number {
    return this.shards.length;
  }

  /** Cristais grandes vivos (Fase 14). */
  get crystalCount(): number {
    return this.crystalList.length;
  }

  /** Entulho vivo e quantos já assentaram no chão. */
  get rubbleStats(): { live: number; resting: number } {
    let resting = 0;
    for (const r of this.rubble) if (r.rest) resting++;
    return { live: this.rubble.length, resting };
  }

  /** Instâncias da camada ambiente no último quadro. */
  ambientCount = 0;

  /**
   * Solta os jorros vivos (não o entulho). Com a aba escondida o quadro para, mas os eventos da rede
   * continuam chegando: os jorros nasciam com idade 0, não envelheciam e voltavam todos juntos na
   * volta (Gate B7). A cena chama isto quando a aba reaparece.
   */
  dropBursts(): void {
    this.shards.length = 0;
    this.crystalList.length = 0;
  }

  /** Sai da cena e solta a geometria e o material (as vitrines, ao parar). */
  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    (this.mesh.material as Material).dispose();
    this.shards.length = 0;
    this.rubble.length = 0;
    this.crystalList.length = 0;
    this.beads.length = 0;
  }

  private updateRubble(list: Rubble[], s: number, i: number): number {
    for (let k = list.length - 1; k >= 0; k--) {
      const p = list[k] as Rubble;
      p.age += s;
      if (p.age >= p.life) {
        list.splice(k, 1);
        continue;
      }
      // Assentado num piso que saiu (a cascata): volta a cair.
      if (p.rest && p.y - ((this.ground?.(p.x, p.z, p.y) ?? -Infinity) + p.size * 0.5) > p.size) p.rest = false;
      if (!p.rest) {
        const from = p.y;
        p.vy -= RUBBLE_GRAVITY * s;
        const drag = Math.max(0, 1 - 0.6 * s);
        p.vx *= drag;
        p.vz *= drag;
        p.x += p.vx * s;
        p.y += p.vy * s;
        p.z += p.vz * s;
        p.angle += p.spin * s;
        // Chão: quica perdendo velocidade e, devagar, assenta parado (o pedaço fica de lado no chão).
        const floor = (this.ground?.(p.x, p.z, from) ?? -Infinity) + p.size * 0.5;
        if (p.y < floor) {
          p.y = floor;
          if (p.vy < 0) p.vy = -p.vy * RESTITUTION;
          p.vx *= FRICTION;
          p.vz *= FRICTION;
          p.spin *= 0.5;
          if (Math.abs(p.vy) < SETTLE_SPEED && Math.hypot(p.vx, p.vz) < SETTLE_SPEED) p.rest = true;
        }
      }
      // Encolhe só no último terço da vida (antes, fica inteiro no chão).
      const f = Math.min(1, (1 - p.age / p.life) * 3);
      const c = p.crystal;
      if (c) this.write(i++, p.x, p.y, p.z, p.ax, p.ay, p.az, p.angle, p.size * f, p.color, c.alpha, c.sx, c.sy, c.sz, 1);
      else this.write(i++, p.x, p.y, p.z, p.ax, p.ay, p.az, p.angle, p.size * f, p.color, 1);
    }
    return i;
  }

  /**
   * Vagalumes (à noite, entre 0,4 e 2,2 m do chão, vagando e piscando) e poeira (de dia, mais alta e
   * lenta), em lugares fixos do mundo: cada um tem um ponto num quadrado de `AMBIENT_SPAN` que se repete,
   * e a câmera vê sempre o quadrado em volta dela. Tudo pelo instante: nada guardado por partícula.
   */
  private writeAmbient(i: number): number {
    const frame = this.ambientFrame;
    this.ambientCount = 0;
    if (!frame || frame.density <= 0) return i;
    const fireflies = Math.round(AMBIENT_MAX * frame.density * frame.night);
    const dust = Math.round(AMBIENT_MAX * 0.5 * frame.density * (1 - frame.night));
    const half = AMBIENT_SPAN / 2;
    const wrap = (seed: number, center: number) => center + ((((seed - center) % AMBIENT_SPAN) + AMBIENT_SPAN) % AMBIENT_SPAN) - half;
    for (let k = 0; k < fireflies + dust; k++) {
      const firefly = k < fireflies;
      // Semente fixa por índice (hash inteiro): o mesmo vagalume no mesmo lugar do mundo.
      const h1 = ((k * 2654435761) >>> 0) / 4294967296;
      const h2 = ((k * 2246822519 + 7) >>> 0) / 4294967296;
      const h3 = ((k * 3266489917 + 13) >>> 0) / 4294967296;
      const t = frame.time * (firefly ? 0.5 : 0.15) + h3 * 6.283;
      const x = wrap(h1 * 997, frame.x) + Math.sin(t) * (firefly ? 0.7 : 1.5);
      const z = wrap(h2 * 991, frame.z) + Math.cos(t * 0.8) * (firefly ? 0.7 : 1.5);
      const floor = this.ground?.(x, z, Infinity) ?? 0;
      const y = floor + (firefly ? 0.4 + h3 * 1.8 + Math.sin(t * 1.7) * 0.2 : 1 + h3 * 4 + Math.sin(t) * 0.5);
      const blink = firefly && !frame.steady ? 0.35 + 0.65 * Math.max(0, Math.sin(frame.time * 2.1 + h1 * 40)) : 1;
      const alpha = firefly ? blink * frame.night : 0.5 * (1 - frame.night);
      this.write(i++, x, y, z, 0, 1, 0, t, firefly ? 0.035 : 0.02, firefly ? this.fireflyColor : this.dustColor, alpha);
      this.ambientCount++;
    }
    return i;
  }

  /** O cinza `gray` (0 a 1) dividido pelo brilho do shader: o entulho sai no cinza dele, sem bloom. */
  private gray(gray: number): Color {
    const key = Math.round(gray * 64);
    let c = this.grays.get(key);
    if (!c) this.grays.set(key, (c = new Color().setScalar(key / 64 / SHARD_GLOW)));
    return c;
  }

  private write(i: number, x: number, y: number, z: number, ax: number, ay: number, az: number, angle: number, size: number, c: Color, alpha: number, sx = 1, sy = 1, sz = 1, crystal = 0): void {
    const len = Math.hypot(ax, ay, az) || 1;
    this.shape.setXYZW(i, sx, sy, sz, crystal);
    this.center.setXYZ(i, x, y, z);
    this.spin.setXYZW(i, ax / len, ay / len, az / len, angle);
    this.size.setX(i, size);
    this.tint.setXYZW(i, c.r, c.g, c.b, alpha);
  }

  private color(hex: string): Color {
    let c = this.colors.get(hex);
    if (!c) this.colors.set(hex, (c = new Color(hex)));
    return c;
  }
}
